import React, { useState, useEffect } from 'react';
import './AdminPanel.css';

function AdminPanel() {
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newAffiliate, setNewAffiliate] = useState({ email: '', password: '', code: '', full_name: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [showAllAffiliates, setShowAllAffiliates] = useState(false);
    const [columnFilters, setColumnFilters] = useState({ name: '', email: '', code: '', eventName: '', status: '' });
    const [monthlyAnalytics, setMonthlyAnalytics] = useState([]);
    const [subscriptionMetrics, setSubscriptionMetrics] = useState(null);
    const [editingEventName, setEditingEventName] = useState(null);
    const [eventNameConfig, setEventNameConfig] = useState({
        eventName: 'affiliate_code_entered',
        eventName_ios: '',
        eventName_android: '',
        eventName_web: '',
        validUntil: '',
        maxUses: ''
    });

    // Determine current month label for leaderboard title
    const currentMonthLabel = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

    useEffect(() => {
        fetchAffiliates();
        fetchSubscriptionMetrics();
    }, []);

    const fetchAffiliates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/.netlify/functions/getAffiliates', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAffiliates(data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching affiliates:', error);
            setError(error.message);
            setLoading(false);
        }
    };

    const fetchSubscriptionMetrics = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/.netlify/functions/getSubscriptionMetrics', {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('Subscription metrics data received:', data);

            // Debug logging to see what data we're getting
            console.log('All time totals:', {
                organic: data.subscriptions?.split?.total?.organic,
                affiliate: data.subscriptions?.split?.total?.affiliate,
                total: data.subscriptions?.total
            });

            setSubscriptionMetrics(data);

            // Use real monthly historical data from the database
            if (data.monthlyHistoricalData) {
                setMonthlyAnalytics(data.monthlyHistoricalData);
            } else {
                // Show error instead of falling back to dummy data
                throw new Error('Monthly historical data is not available. Please check your database connection.');
            }
        } catch (error) {
            console.error('Error fetching subscription metrics:', error);
            setError(error.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAffiliate(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateAffiliate = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/.netlify/functions/createAffiliate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newAffiliate),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('Affiliate created:', result);
            if (result.code_adjusted) {
                window.alert(`Affiliate created. The code "${result.requested_code}" was taken or too short, so "${result.affiliate_code}" was assigned and emailed to them.`);
            } else {
                window.alert(`Affiliate created with code "${result.affiliate_code}".`);
            }
            setNewAffiliate({ email: '', password: '', code: '', full_name: '' });
            fetchAffiliates();
        } catch (error) {
            console.error('Error creating affiliate:', error);
            setError(error.message);
        }
    };

    const handleEditEventName = (affiliate) => {
        const existingConfig = affiliate.payment_info || {};
        setEventNameConfig({
            eventName: existingConfig.eventName || 'affiliate_code_entered',
            eventName_ios: existingConfig.eventName_ios || '',
            eventName_android: existingConfig.eventName_android || '',
            eventName_web: existingConfig.eventName_web || '',
            validUntil: existingConfig.validUntil || '',
            maxUses: existingConfig.maxUses || ''
        });
        setEditingEventName(affiliate);
    };

    const handleEventNameConfigChange = (e) => {
        const { name, value, type } = e.target;
        setEventNameConfig(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const handleUpdateEventName = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');

            // Clean up the config - remove empty strings for optional fields
            const cleanedConfig = {
                eventName: eventNameConfig.eventName,
                eventName_ios: eventNameConfig.eventName_ios || null,
                eventName_android: eventNameConfig.eventName_android || null,
                eventName_web: eventNameConfig.eventName_web || null,
                validUntil: eventNameConfig.validUntil || null,
                maxUses: eventNameConfig.maxUses || null
            };

            const response = await fetch('/.netlify/functions/updateAffiliateOffer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    affiliateId: editingEventName.affiliate_id,
                    ...cleanedConfig
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();
            console.log('EventName updated:', result);
            setEditingEventName(null);
            setEventNameConfig({
                eventName: 'affiliate_code_entered',
                eventName_ios: '',
                eventName_android: '',
                eventName_web: '',
                validUntil: '',
                maxUses: ''
            });
            fetchAffiliates();
        } catch (error) {
            console.error('Error updating eventName:', error);
            setError(error.message);
        }
    };

    const handleColumnFilterChange = (column, value) => {
        setColumnFilters(prev => ({ ...prev, [column]: value }));
    };

    const matchesColumnFilters = (affiliate) => {
        const matchesText = (value, filter) =>
            !filter.trim() || String(value || '').toLowerCase().includes(filter.trim().toLowerCase());

        const eventName = (affiliate.payment_info || {}).eventName || 'affiliate_code_entered';
        return matchesText(affiliate.full_name, columnFilters.name)
            && (matchesText(affiliate.email, columnFilters.email) || matchesText(affiliate.paypal_email, columnFilters.email))
            && matchesText(affiliate.affiliate_code, columnFilters.code)
            && matchesText(eventName, columnFilters.eventName)
            && (!columnFilters.status || (affiliate.is_active ? 'active' : 'inactive') === columnFilters.status);
    };

    const handleCancelEdit = () => {
        setEditingEventName(null);
        setEventNameConfig({
            eventName: 'affiliate_code_entered',
            eventName_ios: '',
            eventName_android: '',
            eventName_web: '',
            validUntil: '',
            maxUses: ''
        });
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="loader"></div>
                <p>Loading admin panel...</p>
            </div>
        );
    }

    if (error) {
        return <div className="error-container">Error: {error}</div>;
    }

    return (
        <div className="admin-panel">
            <div className="dashboard-header">
                <div className="welcome-message">Welcome, Admin! 👋</div>
                <h1>Pep Admin Dashboard</h1>
                <p className="subtitle">Track Pep's performance, manage affiliate partners, and more.</p>
            </div>

            {/* Condensed Key Metrics Table */}
            {subscriptionMetrics && (
                <section className="summary-cards">
                    <div className="stats-card-grid">
                        <div className="stats-card">
                            <div className="card-title">Pending Trials (Organic)</div>
                            <div className="card-value">{subscriptionMetrics.pendingTrials?.organic?.toLocaleString()}</div>
                        </div>
                        <div className="stats-card">
                            <div className="card-title">Pending Trials (Affiliate)</div>
                            <div className="card-value">{subscriptionMetrics.pendingTrials?.affiliate?.toLocaleString()}</div>
                        </div>
                        <div className="stats-card">
                            <div className="card-title">Total Pending Trials</div>
                            <div className="card-value">{subscriptionMetrics.pendingTrials?.total?.toLocaleString()}</div>
                        </div>
                    </div>
                </section>
            )}

            {subscriptionMetrics && (
                <section className="key-metrics-section">
                    <div className="section-header">
                        <h2>Key Metrics Snapshot</h2>
                    </div>

                    <div className="table-container">
                        <table className="key-metrics-table">
                            <thead>
                                <tr>
                                    <th>Metric</th>
                                    <th>Month to Date ({currentMonthLabel})</th>
                                    <th>Trailing 30 Days</th>
                                    <th>Week to Date</th>
                                    <th>Daily Avg (30 days)</th>
                                    <th>Total (All Time)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Organic Subscriptions Row */}
                                <tr>
                                    <td>Organic Sub</td>
                                    <td>{subscriptionMetrics.subscriptions.split.mtd.organic.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.trailing30.organic.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.weekly.organic.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.dailyAverage.organic.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.total.organic.toLocaleString()}</td>
                                </tr>

                                {/* Affiliate Subscriptions Row */}
                                <tr>
                                    <td>Affiliate Sub</td>
                                    <td>{subscriptionMetrics.subscriptions.split.mtd.affiliate.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.trailing30.affiliate.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.weekly.affiliate.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.dailyAverage.affiliate.toLocaleString()}</td>
                                    <td>{subscriptionMetrics.subscriptions.split.total.affiliate.toLocaleString()}</td>
                                </tr>

                                {/* Total Subscriptions Row */}
                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>
                                    <td>Total Sub</td>
                                    <td>{(subscriptionMetrics.subscriptions.split.mtd.organic + subscriptionMetrics.subscriptions.split.mtd.affiliate).toLocaleString()}</td>
                                    <td>{(subscriptionMetrics.subscriptions.split.trailing30.organic + subscriptionMetrics.subscriptions.split.trailing30.affiliate).toLocaleString()}</td>
                                    <td>{(subscriptionMetrics.subscriptions.split.weekly.organic + subscriptionMetrics.subscriptions.split.weekly.affiliate).toLocaleString()}</td>
                                    <td>{(subscriptionMetrics.subscriptions.split.dailyAverage.organic + subscriptionMetrics.subscriptions.split.dailyAverage.affiliate).toLocaleString()}</td>
                                    <td>{(subscriptionMetrics.subscriptions.split.total.organic + subscriptionMetrics.subscriptions.split.total.affiliate).toLocaleString()}</td>
                                </tr>

                                {/* Org Conversion Rate Row */}
                                <tr>
                                    <td>Org Conversion Rate</td>
                                    <td>{subscriptionMetrics.conversionRates.organic.mtd}%</td>
                                    <td>{subscriptionMetrics.conversionRates.organic.monthly}%</td>
                                    <td>{subscriptionMetrics.conversionRates.organic.weekly}%<sup>*</sup></td>
                                    <td>-<sup>**</sup></td>
                                    <td>{subscriptionMetrics.conversionRates.organic.total}%</td>
                                </tr>

                                {/* Affiliate Conversion Rate Row */}
                                <tr>
                                    <td>Affiliate Conversion Rate</td>
                                    <td>{subscriptionMetrics.conversionRates.affiliate.mtd}%</td>
                                    <td>{subscriptionMetrics.conversionRates.affiliate.monthly}%</td>
                                    <td>{subscriptionMetrics.conversionRates.affiliate.weekly}%</td>
                                    <td>-<sup>**</sup></td>
                                    <td>{subscriptionMetrics.conversionRates.affiliate.total}%</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>* Calculated based on trials begun 7-14 days ago to allow for a 7-day trial period to complete</p>
                    <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>** Daily Avg (30 days) is the same conversion rate as Trailing 30 Days.</p>
                </section>
            )}

            <div className="admin-content">
                {/* Monthly Conversions Leaderboard */}
                <section className="leaderboard-section">
                    <div className="section-header">
                        <h2>{currentMonthLabel} Affiliate Leaderboard</h2>
                        <p>Top affiliates by conversions this month</p>
                    </div>
                    <div className="table-container">
                        <table className="analytics-table">
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Affiliate</th>
                                    <th>Conversions</th>
                                    <th>Pending</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Separate affiliates into those with conversions and those with only pending
                                    const withConversions = affiliates.filter(a => Number(a.conversions_current_month || 0) > 0);
                                    const withPendingOnly = affiliates.filter(a => Number(a.conversions_current_month || 0) === 0 && Number(a.pending_conversions || 0) > 0);

                                    // Sort each group
                                    withConversions.sort((a, b) => Number(b.conversions_current_month || 0) - Number(a.conversions_current_month || 0));
                                    withPendingOnly.sort((a, b) => Number(b.pending_conversions || 0) - Number(a.pending_conversions || 0));

                                    // Combine lists
                                    const combined = [...withConversions, ...withPendingOnly];

                                    return combined;
                                })()
                                    .map((affiliate, index) => (
                                        <tr key={affiliate.affiliate_id}>
                                            <td>{index + 1}</td>
                                            <td>{affiliate.full_name}</td>
                                            <td>{(affiliate.conversions_current_month || 0).toLocaleString()}</td>
                                            <td>{(affiliate.pending_conversions || 0).toLocaleString()}</td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="monthly-analytics-section">
                    <div className="section-header">
                        <h2>Monthly Performance</h2>
                        <p>Track your app's performance over the past 12 months</p>
                    </div>

                    <div className="table-container">
                        <table className="analytics-table">
                            <thead>
                                <tr>
                                    <th>Month</th>
                                    <th>Organic Subscriptions</th>
                                    <th>Affiliate Subscriptions</th>
                                    <th>Total Subscriptions</th>
                                    <th>Org Conversion Rate</th>
                                    <th>Affiliate Conversion Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                {monthlyAnalytics.map((month, index) => (
                                    <tr key={index}>
                                        <td className="month-cell">{month.month}</td>
                                        <td className="organic-cell">
                                            <span className="metric-value">{month.subscriptions.organic.toLocaleString()}</span>
                                        </td>
                                        <td className="affiliate-cell">
                                            <span className="metric-value">{month.subscriptions.affiliate.toLocaleString()}</span>
                                        </td>
                                        <td className="total-cell">
                                            <span className="metric-value">{month.subscriptions.total.toLocaleString()}</span>
                                        </td>
                                        <td className="org-conversion-cell">
                                            <span className="metric-value">{
                                                month.month === currentMonthLabel
                                                    ? subscriptionMetrics.conversionRates.organic.mtd
                                                    : month.conversionRate.organic
                                            }%</span>
                                        </td>
                                        <td className="aff-conversion-cell">
                                            <span className="metric-value">{
                                                month.month === currentMonthLabel
                                                    ? subscriptionMetrics.conversionRates.affiliate.mtd
                                                    : month.conversionRate.affiliate
                                            }%</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="create-affiliate-section">
                    <div className="section-header">
                        <h2>Create New Affiliate</h2>
                        <p>Add a new affiliate partner to your program</p>
                    </div>
                    <form onSubmit={handleCreateAffiliate} className="create-affiliate-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="full_name">Full Name</label>
                                <input
                                    type="text"
                                    id="full_name"
                                    name="full_name"
                                    value={newAffiliate.full_name}
                                    onChange={handleInputChange}
                                    placeholder="John Doe"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={newAffiliate.email}
                                    onChange={handleInputChange}
                                    placeholder="john@example.com"
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="password">Password</label>
                                <div className="password-input">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="password"
                                        name="password"
                                        value={newAffiliate.password}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="toggle-password"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? '👁️' : '👁️‍🗨️'}
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="code">Affiliate Code</label>
                                <input
                                    type="text"
                                    id="code"
                                    name="code"
                                    value={newAffiliate.code}
                                    onChange={handleInputChange}
                                    placeholder="AFFILIATE123"
                                    required
                                />
                            </div>
                        </div>
                        <button type="submit" className="create-button">Create Affiliate</button>
                    </form>
                </section>

                <section className="affiliates-list-section">
                    <div className="section-header">
                        <h2>Active Affiliates</h2>
                        <p>Monitor your affiliate partners' performance</p>
                        <div className="filter-toggle">
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={showAllAffiliates}
                                    onChange={(e) => setShowAllAffiliates(e.target.checked)}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span className="toggle-label">
                                {showAllAffiliates ? 'Showing all affiliates' : 'Showing affiliates with trials'}
                            </span>
                        </div>
                    </div>
                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Code</th>
                                    <th>Trials</th>
                                    <th>Conversions</th>
                                    <th>Rate</th>
                                    <th>Event Name</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                                <tr className="column-filters">
                                    <th>
                                        <input
                                            type="text"
                                            placeholder="Search name..."
                                            value={columnFilters.name}
                                            onChange={(e) => handleColumnFilterChange('name', e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            placeholder="Search email..."
                                            value={columnFilters.email}
                                            onChange={(e) => handleColumnFilterChange('email', e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <input
                                            type="text"
                                            placeholder="Search code..."
                                            value={columnFilters.code}
                                            onChange={(e) => handleColumnFilterChange('code', e.target.value)}
                                        />
                                    </th>
                                    <th></th>
                                    <th></th>
                                    <th></th>
                                    <th>
                                        <input
                                            type="text"
                                            placeholder="Search event..."
                                            value={columnFilters.eventName}
                                            onChange={(e) => handleColumnFilterChange('eventName', e.target.value)}
                                        />
                                    </th>
                                    <th>
                                        <select
                                            value={columnFilters.status}
                                            onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                                        >
                                            <option value="">All</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {affiliates
                                    .filter(affiliate => showAllAffiliates || (affiliate.trials_started && affiliate.trials_started > 0))
                                    .filter(matchesColumnFilters)
                                    .map(affiliate => {
                                        const conversionRate = affiliate.trials_started
                                            ? ((affiliate.conversions / affiliate.trials_started) * 100).toFixed(1)
                                            : 0;

                                        const config = affiliate.payment_info || {};
                                        const eventName = config.eventName || 'affiliate_code_entered';
                                        const totalUses = config.currentUses || 0;
                                        const iosUses = config.currentUses_ios || 0;
                                        const androidUses = config.currentUses_android || 0;
                                        const webUses = config.currentUses_web || 0;

                                        return (
                                            <tr key={affiliate.affiliate_id}>
                                                <td>{affiliate.full_name}</td>
                                                <td>
                                                    {affiliate.email}
                                                    {affiliate.paypal_email && affiliate.paypal_email !== affiliate.email && (
                                                        <div className="paypal-email">PayPal: {affiliate.paypal_email}</div>
                                                    )}
                                                </td>
                                                <td><code>{affiliate.affiliate_code}</code></td>
                                                <td>{affiliate.trials_started || 0}</td>
                                                <td>{affiliate.conversions || 0}</td>
                                                <td>{conversionRate}%</td>
                                                <td>
                                                    <div className="event-name-info">
                                                        <div className="event-name">{eventName}</div>
                                                        <div className="usage-breakdown">
                                                            <div className="usage-total">
                                                                Total: {totalUses}{config.maxUses ? `/${config.maxUses}` : ''} uses
                                                            </div>
                                                            <div className="usage-devices">
                                                                <span className="device-usage ios">📱 {iosUses}</span>
                                                                <span className="device-usage android">🤖 {androidUses}</span>
                                                                <span className="device-usage web">🌐 {webUses}</span>
                                                            </div>
                                                        </div>
                                                        {config.validUntil && (
                                                            <div className="expiry-info">
                                                                Expires: {new Date(config.validUntil).toLocaleDateString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status ${affiliate.is_active ? 'active' : 'inactive'}`}>
                                                        {affiliate.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        className="edit-event-btn"
                                                        onClick={() => handleEditEventName(affiliate)}
                                                        title="Edit Event Name Configuration"
                                                    >
                                                        ⚙️
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {/* Event Name Configuration Modal */}
            {editingEventName && (
                <div className="modal-overlay">
                    <div className="modal-content event-config-modal">
                        <div className="modal-header">
                            <h2>Configure Event Name for {editingEventName.full_name}</h2>
                            <button className="close-btn" onClick={handleCancelEdit}>×</button>
                        </div>
                        <form onSubmit={handleUpdateEventName} className="event-config-form">
                            <div className="form-group">
                                <label htmlFor="eventName">Default Event Name</label>
                                <input
                                    type="text"
                                    id="eventName"
                                    name="eventName"
                                    value={eventNameConfig.eventName}
                                    onChange={handleEventNameConfigChange}
                                    placeholder="e.g., vip_offer, holiday_special, limited_time"
                                    required
                                />
                                <small className="form-help">
                                    Default eventName used when no device-specific eventName is set
                                </small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="eventName_ios">iOS Event Name (Optional)</label>
                                <input
                                    type="text"
                                    id="eventName_ios"
                                    name="eventName_ios"
                                    value={eventNameConfig.eventName_ios}
                                    onChange={handleEventNameConfigChange}
                                    placeholder="e.g., vip_offer_ios, mobile_special"
                                />
                                <small className="form-help">
                                    Override eventName for iOS devices
                                </small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="eventName_android">Android Event Name (Optional)</label>
                                <input
                                    type="text"
                                    id="eventName_android"
                                    name="eventName_android"
                                    value={eventNameConfig.eventName_android}
                                    onChange={handleEventNameConfigChange}
                                    placeholder="e.g., vip_offer_android, mobile_special"
                                />
                                <small className="form-help">
                                    Override eventName for Android devices
                                </small>
                            </div>

                            <div className="form-group">
                                <label htmlFor="eventName_web">Web Event Name (Optional)</label>
                                <input
                                    type="text"
                                    id="eventName_web"
                                    name="eventName_web"
                                    value={eventNameConfig.eventName_web}
                                    onChange={handleEventNameConfigChange}
                                    placeholder="e.g., vip_offer_web, desktop_special"
                                />
                                <small className="form-help">
                                    Override eventName for Web browsers
                                </small>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="validUntil">Valid Until (Optional)</label>
                                    <input
                                        type="datetime-local"
                                        id="validUntil"
                                        name="validUntil"
                                        value={eventNameConfig.validUntil}
                                        onChange={handleEventNameConfigChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="maxUses">Max Uses (Optional)</label>
                                    <input
                                        type="number"
                                        id="maxUses"
                                        name="maxUses"
                                        value={eventNameConfig.maxUses}
                                        onChange={handleEventNameConfigChange}
                                        min="1"
                                        placeholder="Leave empty for unlimited"
                                    />
                                </div>
                            </div>

                            <div className="form-actions">
                                <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
                                    Cancel
                                </button>
                                <button type="submit" className="save-btn">
                                    Save Event Name Configuration
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPanel;
